var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import { useSnapshot } from 'valtio';
/** Integrate a React component with [[`useForm`]]
 *
 * This makes it very easy to use the input component with a [[Fields |
 * `fields`]] coming out of `useForm`:
 *
 * ```typescript
 * return <TextInputField field={fields.foo} />
 * ```
 *
 * `useForm` then manages the form state for you, including validation.
 *
 * By convention, we call such a component that works directly with a `field` an
 * **InputField**. We can produce these by wrapping an **Input** component. We
 * use these conventions as postfixes in code, so `TextInput` and
 * `TextInputField`.
 *
 * An Input can be used directly outside the `useForm` framework. It
 * provides the actual input styling and behavior in a consistent manner. To
 * enable it to have a consistent API that can be wrapped, it needs to
 * implement [[`InputProps`]]. You can express this in TypeScript like this:
 *
 * ```typescript
 * import {InputProps} from "../../hooks/useForm";
 *
 * export interface TextInputProps extends InputProps<string> {
 *   // additional props specific to TextInput
 * }
 * ```
 *
 * Once you have implemented your component, you can create the InputField
 * like this:
 *
 * ```typescript
 * export const TextInputField = wrapField(TextInput);
 * ```
 *
 * @typeParam P Type of additional props of the component to wrap
 * @typeParam V Field value type
 *
 * @param Component The **Input** React component to wrap.
 * @returns The **InputField** React component based on the `Component` argument
 */
// Wrap a Component that takes value, onChange, error props
// into a component that just takes a field (from useForm fields)
// eslint-disable-next-line @typescript-eslint/ban-types
export function wrapField(Component) {
    const Result = (props) => {
        const { id, field, label } = props, rest = __rest(props, ["id", "field", "label"]);
        // we want synchronous field updates, because otherwise we break cursor behavior
        // in controlled components
        // https://dev.to/kwirke/solving-caret-jumping-in-react-inputs-36ic
        const fieldSnap = useSnapshot(field, { sync: true });
        if (!fieldSnap.visible) {
            return <></>;
        }
        return (<Component id={id || fieldSnap.name} label={label} value={fieldSnap.value} onChange={(value) => {
                field.setValue(value);
            }} error={fieldSnap.error} ref={fieldSnap.ref} {...rest}/>);
    };
    Result.displayName = Component.displayName;
    return Result;
}
